import PropTypes from 'prop-types';
import { useState } from 'react';
import exifr from "exifr";
import imageCompression from 'browser-image-compression';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
// components
import { Autocomplete, Box, CircularProgress, Divider, FormControlLabel, MenuItem, Switch } from '@mui/material';
import { useDebounce } from 'src/hooks/use-debounce';
import { Upload } from 'src/components/upload';
import { useGetCategoriesQuery, useGetSuggestionCategoriesQuery } from 'src/store/Reducer/category';

export default function FileManagerNewFolderDialog({
  title = 'Upload Files',
  open,
  onClose,
  onCreate,
  onUpdate,
  folderName,
  onChangeFolderName,
  currentFolder,
  files,
  setFiles,
  selectedCategory,
  setSelectedCategory,
  creatingLoading,
  caption,
  setCaption,
  confidential,
  onToggleConfidential,
  ...other
}) {

  const [videoLoading, setVideoLoading] = useState(false);
  const [folderSearch, setFolderSearch] = useState('');
  const debouncedFolderSearch = useDebounce(folderSearch, 400);


  const { data: { data = [] } = {} } = useGetCategoriesQuery({
    page: 0,
    limit: 1000,
    search: '',
    status: 'active',
  });


  const { data: { data: suggestionCategories = [] } = {} } = useGetSuggestionCategoriesQuery(
    {
      page: 0,
      limit: 150,
      search: debouncedFolderSearch,
    },
    { skip: !debouncedFolderSearch }
  );

 function compressVideoSimple(file, maxWidth = 480, targetFps = 15) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.src = URL.createObjectURL(file);
    video.muted = true;
    video.crossOrigin = "anonymous";

    video.onloadeddata = () => {
      let outputWidth = video.videoWidth > maxWidth ? maxWidth : video.videoWidth;
      let scale = outputWidth / video.videoWidth;
      let outputHeight = video.videoHeight * scale;

      const canvas = document.createElement("canvas");
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      const ctx = canvas.getContext("2d");

      const frameInterval = 1000 / targetFps;
      let lastFrameTime = 0;

      const stream = canvas.captureStream(targetFps);
      const recorder = new MediaRecorder(stream, {
        mimeType: "video/webm",
        videoBitsPerSecond: 450_000,
      });

      let chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => resolve(new Blob(chunks, { type: "video/webm" }));

      recorder.start();

      const drawFrame = (now) => {
        if (!video.paused && !video.ended) {
          if (now - lastFrameTime >= frameInterval) {
            ctx.drawImage(video, 0, 0, outputWidth, outputHeight);
            lastFrameTime = now;
          }
          requestAnimationFrame(drawFrame);
        } else {
          recorder.stop();
        }
      };

      video.play();
      requestAnimationFrame(drawFrame);
    };

    video.onerror = reject;
  });
}


const handleDrop = async (acceptedFiles) => {
  try {
    setVideoLoading(true);
    const compressedFiles = [];

    for (let file of acceptedFiles) {

      // ------------------------------------------
      // 1️⃣ Extract GPS coordinates from image 
      // ------------------------------------------
      let gps = null;
      if (file.type.startsWith("image/")) {
        try {
          gps = await exifr.gps(file);
        } catch (err) {
          console.log("No EXIF GPS found:", err);
        }
      }
      if (file.type.startsWith('image/')) {
        // const compressedImage = await imageCompression(file, {
        //   maxSizeMB: 1,
        //   maxWidthOrHeight: 1920,
        //   initialQuality: 0.9,
        //   useWebWorker: true,
        // });

        // compressedImage.preview = URL.createObjectURL(compressedImage);
        // compressedImage.gps = gps;  // 👈 Attach GPS inside file object
        // compressedFiles.push(compressedImage);

        // continue;

         file.preview = URL.createObjectURL(file);
         file.lat= gps?.latitude || null;
         file.lng= gps?.longitude || null;
        compressedFiles.push(file);
      }
      if (file.type.startsWith('video/')) {
        const compressedVideoBlob = await compressVideoSimple(file, 480);
        const compressedVideoFile = new File([compressedVideoBlob], file.name, { type: file.type });
        compressedVideoFile.preview = URL.createObjectURL(compressedVideoFile);
        compressedFiles.push(compressedVideoFile);
        continue;
      }
    }

    setFiles((prev) => [...prev, ...compressedFiles]);

  } catch (error) {
    console.error("File processing failed:", error);
  } finally {
    setVideoLoading(false);
  }
};

  const handleRemoveFile = (inputFile) => {
    const filtered = files.filter((file) => file !== inputFile);
    setFiles(filtered);
  };

  const handleRemoveAllFiles = () => {
    setFiles([]);
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose} {...other}>
      <DialogTitle
        sx={{
          p: (theme) => theme.spacing(3, 3, 2, 3),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {title}

        <FormControlLabel
          control={
            <Switch value={confidential} checked={confidential} onChange={onToggleConfidential} />
          }
          label="Confidential"
        />
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1, pb: 0, border: 'none' }}>
        {(onCreate || onUpdate) && (
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              freeSolo
              fullWidth
              options={folderSearch ? suggestionCategories?.map((item) => item || "") : []}
              value={folderName}
              onInputChange={(event, newInputValue) => {
                setFolderSearch(newInputValue);
                if (onChangeFolderName) {
                  onChangeFolderName({ target: { value: newInputValue } });
                }
              }}
              clearIcon={folderSearch ? undefined : null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Folder name"
                  sx={{ mb: 3 }}
                />
              )}
            />
            <TextField
              fullWidth
              select
              placeholder="Select Category Type"
              label="Category Type"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="">Select Category</MenuItem>
              <Divider sx={{ borderStyle: 'dashed' }} />
              {data?.map((category) => (
                <MenuItem key={category?.id} value={category?.id}>
                  {category?.title}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Description"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              sx={{ mt: 3 }}
            />
          </Box>
        )}
        {title === 'Upload Files' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField fullWidth select placeholder="Select Category Type" label="Category Type">
              <MenuItem value="">Select Category</MenuItem>
              <Divider sx={{ borderStyle: 'dashed' }} />
              {data?.map((category) => (
                <MenuItem key={category?.id} value={category?.id}>
                  {category?.title}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Folder name"
              value={folderName}
              onChange={onChangeFolderName}
              sx={{ mb: 3 }}
            />
          </Box>
        )}

        <Upload multiple files={files} onDrop={handleDrop} onRemove={handleRemoveFile} thumbnail />
      </DialogContent>

      <DialogActions>
        {!!files?.length && (
          <Button variant="outlined" color="inherit" onClick={handleRemoveAllFiles}>
            Remove all
          </Button>
        )}

        {videoLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={22} thickness={5} />
            <span>Compressing video...</span>
          </Box>
        )}

        {(onCreate || onUpdate) && (
          <Stack direction="row" justifyContent="flex-end" flexGrow={1}>
            <LoadingButton
              variant="soft"
              onClick={onCreate || onUpdate}
              loading={creatingLoading}
              disabled={creatingLoading}
            >
              {onUpdate ? 'Save' : 'Create'}
            </LoadingButton>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
}

FileManagerNewFolderDialog.propTypes = {
  folderName: PropTypes.string,
  onChangeFolderName: PropTypes.func,
  onClose: PropTypes.func,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func,
  open: PropTypes.bool,
  title: PropTypes.string,
  files: PropTypes.array,
  setFiles: PropTypes.func,
  selectedCategory: PropTypes.string,
  setSelectedCategory: PropTypes.func,
  creatingLoading: PropTypes.bool,
};
